import { useCallback, useMemo } from 'react';
import flatten from 'lodash.flatten';
import { InfiniteQueryObserverResult } from 'react-query';

interface IPagedGroup {
  hasMoreItems?: boolean;
  data: any;
  totalItems?: number;
}

export interface IInfiniteQueryMemo<TData> {
  dataMemo: {
    reset?: boolean;
    hasMoreItems?: boolean;
    data?: TData[];
    page?: number;
    nextPage?: number;
    totalItems?: number;
  };
  fetchNextPageCallback: () => Promise<InfiniteQueryObserverResult<TData>>;
  query: Partial<InfiniteQueryObserverResult<TData>>;
}

export function useInfiniteQueryDataMemo<
  TData extends IPagedGroup | IPagedGroup[]
>({
  data,
  fetchNextPage,
  ...query
}: InfiniteQueryObserverResult<TData>): IInfiniteQueryMemo<TData> {
  const { isLoading } = query;
  const dataMemo = useMemo(() => {
    if (!isLoading && data) {
      const { pages } = data;
      const flattened = flatten(pages);
      const { hasMoreItems, totalItems } = flattened[flattened.length - 1];
      return {
        reset: flattened.length === 1,
        hasMoreItems,
        data: flatten(flattened.map(f => f.data)),
        page: flattened.length,
        nextPage: hasMoreItems ? flattened.length + 1 : undefined,
        totalItems,
      };
    }
    return {};
  }, [data, isLoading]);
  const fetchNextPageCallback = useCallback(() => fetchNextPage(), [
    fetchNextPage,
  ]);
  return { dataMemo, fetchNextPageCallback, query };
}
